import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { useRole } from '@/hooks/use-role';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useOneShotHighlight } from '@/hooks/useOneShotHighlight';
import { useScopedToast } from '@/hooks/useScopedToast';
import { downloadCsvFile } from '@/lib/download';
import { dateFnsLocaleFor, formatPeriodEnds } from '@/lib/format-dates';
import { scrollIntoViewBelowActionBar } from '@/lib/scroll-container';
import { cn } from '@/lib/utils';
import { EmptyChart } from './charts/ChartPrimitives';
import { ThreePlUsageTotal } from './ThreePlUsageTotal';
import { ShippedPerDayChart } from './ShippedPerDayChart';
import { ThreePlClientsTable } from './ThreePlClientsTable';
import { ThreePlBill, type BillLineActions } from './ThreePlBill';
import { CountCell, MoneyCell } from '@/components/shared/NumberCells';
import { NO_PRICE_SELECTOR, Swatch } from './ThreePlCells';
import { AddToBillDialog } from './AddToBillDialog';
import { RemoveBillLineDialog } from './RemoveBillLineDialog';
import { useThreePlBillLines, useThreePlUsage } from '../hooks/useReportingV2';
import { updateSearchParams } from '@/lib/search-params';
import { toReportingDateParam } from '../lib/reporting-url-state';
import { buildThreePlUsageCsv, threePlUsageExportFilename } from '../lib/three-pl-usage-export';
import {
  BILL_FOCUS_PARAM,
  LINE_TITLE_KEYS,
  THREE_PL_USAGE_MAX_DAYS,
  billServicesFor,
  billableRows,
  buildUsageView,
  cell,
  defaultSortDir,
  formatUsageDay,
  kindQty,
  lineCount,
  lineFilter,
  notShippedOf,
  ownerStockOf,
  parseBillLine,
  periodDays,
  resolveScopeClientId,
  resolveSkuSort,
  searchRows,
  skuColumnChars,
  skuSortParams,
  skuTableMinWidthRem,
  sortSkuRows,
  type BillExtraRowView,
  type BillLineId,
  type SkuSortField,
  type SortDir,
  type ThreePlUsageRow,
} from '../lib/three-pl-usage-view';

interface ThreePlUsageTabProps {
  organizationId: string | null;
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  /** Only true when this tab is active and the period is ready: gates the call. */
  active: boolean;
}

const ALL_CLIENTS = '__all__';

// One-line rows (py-2.5 + a 20px line + the border). Dynamic measurement
// corrects each row after it mounts; this is only the first-paint estimate.
const ESTIMATED_ROW_HEIGHT = 41;

/** Padding and alignment of the SKU table's count cells (`px-1.5`: the widths in `skuTableMinWidthRem`). */
const COUNT_TD = 'px-1.5 py-2.5 text-right';

/**
 * The total panel and the chart. `grid-cols-1` is a `minmax(0,1fr)` track:
 * without a column rule below `lg` the one implicit track is `auto`, which
 * grows to the cards' widest unbreakable content and pushes the page sideways
 * on a phone.
 */
const CARDS_GRID = 'grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]';

/**
 * "3PL usage": the tab reads as a bill. For one 3PL location and one period:
 * the money to bill and pieces shipped per day, then one row per client with
 * To bill and View bill (All clients) or one priced bill (one client), then
 * every SKU on the bill(s) with its amount, search, sort, a bill-line filter
 * and CSV.
 *
 * ONE call per (location, period). Client, bill line, search and sort live in
 * the URL (`uclient`, `uline`, `uq`, `usort`, `udir`) and run in memory over the
 * full response, on the same layer as the counts. `ufocus` (Back to bill from
 * Client prices) is consumed once: the bill lands on that line, then the param
 * is removed. The four children are memoised and fed only values from the
 * memoised view, so search and sort never re-render them.
 *
 * Money is always in the response's `currency` (the 3PL's, D2), never the
 * viewer's own org currency. No-price warnings, Set price, Add a service to
 * this bill and removing a hand-added line reach only an owner holding
 * `skus.costs_manage` (D28); every other viewer gets the amounts alone. Adding
 * or removing a line is ONE call, then one refetch of the report.
 */
export const ThreePlUsageTab: React.FC<ThreePlUsageTabProps> = ({
  organizationId,
  startDate,
  endDate,
  locationId,
  active,
}) => {
  const { t, i18n } = useTranslation('inventory');
  const { can } = useRole();
  const { format } = useCurrencyFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const pickerId = React.useId();
  const skuHeadingId = React.useId();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const from = toReportingDateParam(startDate);
  const to = toReportingDateParam(endDate);
  const tooLong = periodDays(from, to) > THREE_PL_USAGE_MAX_DAYS;

  const query = useThreePlUsage({
    organizationId,
    locationId,
    from,
    to,
    enabled: active && locationId != null && !tooLong,
  });
  // A failed read is never shown with the previous answer (error wins).
  const report = query.isError ? undefined : query.data;

  // react-router hands out a new setSearchParams whenever the URL changes, so
  // the handlers read it through a ref: they stay referentially stable and the
  // memoised children do not re-render on every keystroke.
  const setParamsRef = React.useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  const updateParams = React.useCallback((updates: Readonly<Record<string, string | null>>) => {
    setParamsRef.current(
      (previous) => updateSearchParams(previous, updates),
      { replace: true },
    );
  }, []);

  const clientParam = searchParams.get('uclient');
  const lineParam = searchParams.get('uline');
  const searchText = searchParams.get('uq') ?? '';
  const sortParam = searchParams.get('usort');
  const dirParam = searchParams.get('udir');

  const scopeClientId = report ? resolveScopeClientId(report, clientParam) : null;
  // The only caller who sees no-price warnings and Set price (D28): an owner
  // viewer holding skus.costs_manage, which gates the price and bill-line writes.
  const canFix = report?.viewer === 'owner' && can('skus.costs_manage');
  const view = React.useMemo(
    () => (report ? buildUsageView(report, scopeClientId, { canFix }) : null),
    [report, scopeClientId, canFix],
  );
  const currency = report?.currency ?? null;
  const formatValue = React.useCallback(
    (value: number) => format(value, currency),
    [format, currency],
  );

  // ── Bill lines (owner `skus.costs_manage` only, D28): Add a service, remove a hand-added line ─
  const { addLine, removeLine } = useThreePlBillLines();
  // The bill's success and info toasts close when this tab unmounts; errors stay (§15.5).
  const notify = useScopedToast();
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<BillExtraRowView | null>(null);
  const addButtonRef = React.useRef<HTMLButtonElement>(null);
  const removeTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const billClientId = view?.client?.id ?? null;
  const clientServices = React.useMemo(
    () => (report && canFix && billClientId ? billServicesFor(report, billClientId) : []),
    [report, canFix, billClientId],
  );
  const canAdd = clientServices.length > 0;
  const openAdd = React.useCallback(() => setAdding(true), []);
  const openRemove = React.useCallback((row: BillExtraRowView, trigger: HTMLButtonElement) => {
    removeTriggerRef.current = trigger;
    setRemoving(row);
  }, []);
  // Stable while nothing it holds changes: the bill is memoised.
  const billActions = React.useMemo<BillLineActions | null>(
    () => (canFix && billClientId ? { canAdd, onAdd: openAdd, onRemove: openRemove, addButtonRef } : null),
    [canFix, billClientId, canAdd, openAdd, openRemove],
  );
  // A bill-line dialog never outlives the bill it was opened on.
  React.useEffect(() => {
    setAdding(false);
    setRemoving(null);
  }, [billClientId]);
  // ── Jumps: "Not final" (owner `skus.costs_manage` only, C3) and Back to bill (P1) ─
  // The target takes focus (the warning, a line's toggle, a service's name)
  // and its row the one-shot tint (DESIGN.md §5). Stable: the total panel is
  // memoised.
  const { highlighted: highlightedRow, highlight } = useOneShotHighlight<string>();
  /** Scroll a jump target's row below the action bar, focus it, tint the row once (DESIGN.md §5). */
  const landOn = React.useCallback(
    (target: HTMLElement, key: string | undefined) => {
      scrollIntoViewBelowActionBar(target.closest('tr') ?? target);
      target.focus({ preventScroll: true });
      if (key) highlight(key);
    },
    [highlight],
  );
  const showNoPrice = React.useCallback(() => {
    const warning = rootRef.current?.querySelector<HTMLElement>(NO_PRICE_SELECTOR);
    if (warning) landOn(warning, warning.dataset.noPrice);
  }, [landOn]);

  // Back to bill (P1): land on the line Set price left from, once the FRESH
  // bill is on screen. No Save: the cached report is fresh, so it lands at
  // once with no call. A Save marked the report stale: it lands after the one
  // refetch, on the priced amount. Consumed once, found or not; a failed read
  // drops it. All clients carries no anchors, so nothing matches there.
  const focusParam = searchParams.get(BILL_FOCUS_PARAM);
  const landedRef = React.useRef<string | null>(null); // StrictMode runs a mount effect twice
  React.useEffect(() => {
    if (focusParam === null || landedRef.current === focusParam) return;
    if (query.isError) {
      landedRef.current = focusParam;
      updateParams({ [BILL_FOCUS_PARAM]: null });
      return;
    }
    if (!view || query.isFetching) return;
    landedRef.current = focusParam;
    updateParams({ [BILL_FOCUS_PARAM]: null });
    const target = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-bill-anchor]') ?? []).find(
      (el) => el.dataset.billAnchor === focusParam,
    );
    if (target) landOn(target, focusParam);
  }, [focusParam, view, query.isFetching, query.isError, updateParams, landOn]);

  const allScope = view?.scope === 'all';
  // A line that is not on the bill (Qty 0, P3) filters nothing.
  const parsedLine = view?.scope === 'one' ? parseBillLine(lineParam) : null;
  const line: BillLineId | null =
    parsedLine && view?.bill && view.bill.lines[parsedLine].qty > 0 ? parsedLine : null;
  // Unchosen, the sort follows the line filter (This line while it is on, else
  // Shipped); a field that no longer fits falls back at its own direction.
  const { field: sortField, dir: sortDir } = resolveSkuSort(sortParam, dirParam, { allScope, line });

  // SKU pipeline, all in memory: billable rows, then the bill line, then the
  // search, then the sort. It never feeds back into `view`.
  const billable = React.useMemo(() => (view ? billableRows(view.scopeRows) : []), [view]);
  const skuChars = React.useMemo(() => skuColumnChars(billable), [billable]);
  const lineRows = React.useMemo(
    () => (line && view?.client ? lineFilter(billable, view.client.id, line) : billable),
    [billable, line, view],
  );
  const searched = React.useMemo(() => searchRows(lineRows, searchText), [lineRows, searchText]);
  const sorted = React.useMemo(
    () => (view ? sortSkuRows(searched, sortField, sortDir, view.clientsById, line) : []),
    [searched, sortField, sortDir, view, line],
  );

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 12,
    initialRect: { width: 1024, height: 460 },
    getItemKey: (index) => {
      const row = sorted[index];
      return row ? `${row.clientOrgId}:${row.sku}` : index;
    },
  });
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [sorted]);

  const openClient = React.useCallback(
    (clientId: string) => updateParams({ uclient: clientId, uline: null }),
    [updateParams],
  );
  const backToAll = React.useCallback(
    () => updateParams({ uclient: null, uline: null }),
    [updateParams],
  );
  const toggleLine = React.useCallback((next: BillLineId) => {
    setParamsRef.current(
      (previous) =>
        updateSearchParams(previous, {
          // A step 1 value (`printed`) is the same line as its alias: a click clears it.
          uline: parseBillLine(previous.get('uline')) === next ? null : next,
        }),
      { replace: true },
    );
  }, []);
  const clearLine = () => {
    updateParams({ uline: null });
    searchInputRef.current?.focus();
  };
  const clearSearch = () => {
    updateParams({ uq: null });
    searchInputRef.current?.focus();
  };
  const handlePick = (value: string) => {
    if (value === ALL_CLIENTS) backToAll();
    else openClient(value);
  };
  const handleSort = (field: SkuSortField) => {
    const nextDir: SortDir =
      field === sortField ? (sortDir === 'asc' ? 'desc' : 'asc') : defaultSortDir(field);
    updateParams(skuSortParams(field, nextDir, line));
  };

  const language = i18n.language;
  // The period carries its year once, on its end (DESIGN.md §17). A plain
  // string: the memoised children compare it by value.
  const rangeLabel = report
    ? t(
        'reporting.threePlUsage.range',
        formatPeriodEnds(report.period.from, report.period.to, dateFnsLocaleFor(language)),
      )
    : '';

  const handleExport = () => {
    if (!report || !view) return;
    downloadCsvFile(
      buildThreePlUsageCsv(sorted, {
        viewer: report.viewer,
        ownerName: report.location.ownerName,
        clientsById: view.clientsById,
        location: report.location,
        period: report.period,
        currency: report.currency,
      }),
      threePlUsageExportFilename(report.period.from, report.period.to),
    );
  };

  // ── States that make no call or have nothing to show ─────────────────────
  if (locationId == null) {
    return <EmptyChart message={t('reporting.threePlUsage.pickLocation')} />;
  }
  if (tooLong) {
    return (
      <EmptyChart
        message={t('reporting.threePlUsage.periodTooLong', { max: THREE_PL_USAGE_MAX_DAYS })}
      />
    );
  }
  if (query.isError && query.error?.data?.code === 'FORBIDDEN') {
    return <EmptyChart message={t('reporting.threePlUsage.forbidden')} />;
  }
  if (report && report.viewer === 'owner' && report.clients.length === 0) {
    return <EmptyChart message={t('reporting.threePlUsage.noClients')} />;
  }

  // ── Context row (D29): basis and range, then the Client picker and Export ─
  const contextRow = (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {report && (
          <>
            <span title={report.location.timezone}>
              {report.location.timezone === 'UTC'
                ? t('reporting.threePlUsage.basisUtc')
                : t('reporting.threePlUsage.basis', { location: report.location.name })}
            </span>
            <span className="h-3 w-px bg-border" aria-hidden="true" />
            <span className="tabular-nums">{rangeLabel}</span>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {report?.viewer === 'owner' && (
          <div className="flex items-center gap-2">
            <Label htmlFor={pickerId} className="text-xs font-medium text-muted-foreground">
              {t('reporting.threePlUsage.client')}
            </Label>
            <Select value={scopeClientId ?? ALL_CLIENTS} onValueChange={handlePick}>
              <SelectTrigger id={pickerId} className="h-8 w-[200px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLIENTS} className="text-xs">
                  {t('reporting.threePlUsage.allClientsCount', { count: report.clients.length })}
                </SelectItem>
                {report.clients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="text-xs">
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          className="h-8 gap-1.5 px-3 text-xs"
          title={t('reporting.threePlUsage.exportTitle')}
          onClick={handleExport}
          disabled={!report || sorted.length === 0}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          {t('reporting.threePlUsage.exportCsv', { count: sorted.length })}
        </Button>
      </div>
    </div>
  );

  if (query.isError) {
    return (
      <div ref={rootRef} className="space-y-4">
        {contextRow}
        <SectionError
          variant="block"
          body={t('reporting.threePlUsage.error')}
          onRetry={() => void query.refetch()}
          retryLabel={t('retry', { ns: 'common' })}
          retrying={query.isFetching}
        />
      </div>
    );
  }

  if (!report || !view) {
    return (
      <div className="space-y-4">
        {contextRow}
        <div className="space-y-4" aria-busy="true">
          <div className={CARDS_GRID}>
            <Skeleton className="h-[248px] rounded-lg" />
            <Skeleton className="h-[248px] rounded-lg" />
          </div>
          <Skeleton className="h-40 rounded-lg" />
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Nothing in scope (no row, charge or hand-added line). An owner holding
  // `skus.costs_manage` on one client's bill still gets the bill, so a service
  // can be added to a period with nothing shipped yet (D3).
  if (view.empty && !(billActions && view.scope === 'one')) {
    return (
      <div className="space-y-4">
        {contextRow}
        <EmptyChart message={t('reporting.threePlUsage.empty')} />
      </div>
    );
  }

  // ── Total panel heading: the 3PL reads "To bill", a client "Your <3PL> bill" ─
  const ownerName = report.location.ownerName;
  const totalTitle =
    report.viewer === 'client'
      ? t('reporting.threePlUsage.total.clientBill', { owner: ownerName, range: rangeLabel })
      : view.client
        ? t('reporting.threePlUsage.total.toBillClient', { client: view.client.name, range: rangeLabel })
        : t('reporting.threePlUsage.total.toBillAll', { range: rangeLabel });

  // ── Bill-line dialogs: where focus goes when they close ────────────────────
  const handleAdded = ({ chargeDate }: { chargeDate: string }) => {
    setAdding(false);
    notify({
      title: t('reporting.threePlUsage.toast.addedOn', { date: formatUsageDay(chargeDate, language) }),
    });
  };
  // Radix has no trigger to return to: both close paths land on the button that opened it.
  const focusAddButton = (event: Event) => {
    event.preventDefault();
    addButtonRef.current?.focus();
  };
  const closeRemove = () => setRemoving(null);
  // Kept: back to that row's remove button. Gone: its row went with it, so
  // Add a service to this bill is the nearest control that still exists.
  const handleRemoveClosed = (event: Event, gone: boolean) => {
    event.preventDefault();
    const trigger = removeTriggerRef.current;
    removeTriggerRef.current = null;
    if (!gone && trigger?.isConnected) trigger.focus();
    else addButtonRef.current?.focus();
  };

  // ── SKU table ─────────────────────────────────────────────────────────────
  // While a bill line filters it (C2), "This line" holds that line's pieces on
  // each SKU (the column the line's Qty adds up from), and Amount is renamed
  // "SKU total": it stays the SKU's whole bill, never this line's money.
  const lineColumn = line !== null;
  const columnCount = (allScope ? 10 : 9) + (lineColumn ? 1 : 0);
  // D19: "This line" on the cancelled-garment line counts every cancelled
  // piece on the 3PL's stock, film-only ones too (the rows carry no
  // "decorated" flag); this says how many of them the line does not charge.
  const filmOnly = line === 'cancelledGarment' ? (view.bill?.cancelledFilmOnly ?? 0) : 0;
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? totalSize) : 0;

  const sortHeader = (field: SkuSortField, label: string, align: 'left' | 'right' = 'right') => {
    const isActive = sortField === field;
    return (
      <th
        key={field}
        scope="col"
        aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
        className={cn(
          'py-2 align-bottom text-xs font-medium text-muted-foreground',
          align === 'right' ? 'px-1.5 text-right' : 'px-3 text-left',
        )}
      >
        {/* Capped at the cell so a long owner name wraps inside its own
            column instead of running over the next header. The glyph sits
            UNDER the label (DESIGN.md §14 exception): it costs no width, every
            glyph lines up on one row on its column's edge, and the header
            keeps its height whichever column is sorted. */}
        <button
          type="button"
          onClick={() => handleSort(field)}
          className={cn(
            'inline-flex max-w-full flex-col gap-0.5 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            align === 'right' ? 'items-end text-right' : 'items-start text-left',
            isActive && 'text-foreground',
          )}
        >
          <span className="min-w-0 max-w-full break-words text-balance">{label}</span>
          {isActive ? (
            sortDir === 'asc' ? (
              <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />
            ) : (
              <ArrowDown className="h-3 w-3 shrink-0" aria-hidden="true" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 shrink-0 opacity-40" aria-hidden="true" />
          )}
        </button>
      </th>
    );
  };

  const renderRow = (row: ThreePlUsageRow, index: number) => {
    const client = view.clientsById.get(row.clientOrgId);
    const ownerStock = ownerStockOf(row);
    return (
      <tr
        key={`${row.clientOrgId}:${row.sku}`}
        data-index={index}
        ref={rowVirtualizer.measureElement}
        className="border-b last:border-0 hover:bg-muted/40 motion-reduce:hover:bg-muted/40"
      >
        {allScope && (
          <td className="py-2.5 pl-3 pr-2">
            {/* The name truncates to keep the table inside 1280px (P6); the
                full name is in `title`, read by screen readers from the DOM,
                and shown in full in the clients table, picker and legend. */}
            <span className="flex min-w-0 items-center gap-2">
              <Swatch colour={client?.colour} />
              <span className="min-w-0 truncate text-xs" title={client?.name}>
                {client?.name}
              </span>
            </span>
          </td>
        )}
        {/* The code in full: the column fits the longest one up to a cap, and
            a longer code wraps (at a hyphen where it can) instead of clipping. */}
        <td className="px-3 py-2.5 font-mono text-xs font-medium [overflow-wrap:anywhere]">
          {row.sku}
        </td>
        <td
          className="truncate px-3 py-2.5 text-muted-foreground"
          title={row.title ?? undefined}
        >
          {row.title ?? '-'}
        </td>
        {line && <CountCell value={lineCount(row, line)} strong className={COUNT_TD} />}
        <CountCell value={row.shipped} strong={!line} className={COUNT_TD} />
        <CountCell value={kindQty(row, 'printed')} className={COUNT_TD} />
        <CountCell value={kindQty(row, 'pickPack')} className={COUNT_TD} />
        <CountCell value={kindQty(row, 'blank')} className={COUNT_TD} />
        <td className="whitespace-nowrap px-1.5 py-2.5 text-right tabular-nums">
          {ownerStock === 0 ? (
            <span className="text-muted-foreground">{cell(0)}</span>
          ) : (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary-strong">
              {cell(ownerStock)}
            </span>
          )}
        </td>
        <CountCell value={notShippedOf(row)} className={COUNT_TD} />
        <MoneyCell value={row.amount} formatValue={formatValue} className={COUNT_TD} />
      </tr>
    );
  };

  return (
    <div ref={rootRef} className="space-y-4">
      {contextRow}

      <div className={CARDS_GRID}>
        <ThreePlUsageTotal
          title={totalTitle}
          scope={view.scope}
          amount={view.amount}
          unit={report.currency}
          segments={view.segments}
          formatValue={formatValue}
          pieces={view.total}
          ownerStock={view.ownerStock}
          orders={view.orders}
          ownerName={ownerName}
          noPrice={view.noPrice}
          onShowNoPrice={showNoPrice}
        />
        <ShippedPerDayChart chart={view.chart} rangeLabel={rangeLabel} />
      </div>

      {view.clientRows && view.footer ? (
        <ThreePlClientsTable
          rows={view.clientRows}
          footer={view.footer}
          ownerName={ownerName}
          formatValue={formatValue}
          onOpen={openClient}
          highlightedRow={highlightedRow}
        />
      ) : view.client && view.bill ? (
        <ThreePlBill
          client={view.client}
          bill={view.bill}
          ownerName={ownerName}
          orders={view.orders}
          topUps={view.topUps}
          redone={view.redone}
          formatValue={formatValue}
          selectedLine={line}
          onToggleLine={toggleLine}
          onBack={report.viewer === 'owner' ? backToAll : null}
          actions={billActions}
          highlightedRow={highlightedRow}
        />
      ) : null}

      {billActions && view.client && (
        <>
          <AddToBillDialog
            open={adding}
            onOpenChange={setAdding}
            client={view.client}
            locationId={report.location.id}
            services={clientServices}
            timeZone={report.location.timezone}
            period={report.period}
            formatValue={formatValue}
            addLine={addLine}
            onAdded={handleAdded}
            onCloseAutoFocus={focusAddButton}
          />
          <RemoveBillLineDialog
            row={removing}
            clientName={view.client.name}
            formatValue={formatValue}
            notify={notify}
            removeLine={removeLine}
            onClose={closeRemove}
            onCloseAutoFocus={handleRemoveClosed}
          />
        </>
      )}

      {/* Unlabelled on purpose: the scroll region below carries the heading as
          its name, so the page has one "Every SKU" landmark, not two. */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <h3 id={skuHeadingId} className="text-base font-semibold leading-tight">
              {allScope
                ? t('reporting.threePlUsage.sku.titleAll')
                : line
                  ? t('reporting.threePlUsage.sku.titleLine')
                  : t('reporting.threePlUsage.sku.titleOne')}
            </h3>
            <p className="mt-1 max-w-prose text-xs text-muted-foreground">{t('reporting.threePlUsage.sku.hint')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {line && view.client && (
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 py-1 pl-3 pr-1 text-xs text-primary-strong">
                <span>
                  {t('reporting.threePlUsage.sku.chip', {
                    client: view.client.name,
                    line: t(LINE_TITLE_KEYS[line], { owner: ownerName }),
                    skus: t('reporting.threePlUsage.count.skus', { count: lineRows.length }),
                  })}
                </span>
                <button
                  type="button"
                  onClick={clearLine}
                  className="rounded-full px-2 py-0.5 font-semibold hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('reporting.threePlUsage.sku.showAll')}
                </button>
              </span>
            )}
            <div className="relative w-full sm:w-64">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                ref={searchInputRef}
                value={searchText}
                onChange={(event) => updateParams({ uq: event.target.value })}
                placeholder={t('reporting.threePlUsage.sku.search')}
                aria-label={t('reporting.threePlUsage.sku.search')}
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          role="region"
          aria-labelledby={skuHeadingId}
          tabIndex={0}
          className="max-h-[460px] overflow-auto rounded-lg border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {/* All clients, and one bill with This line, fit a 1280px window
              with the sidebar open: 960px of page less a 17px page scrollbar,
              this region's 2px border and its own 17px scrollbar (Windows
              classic) is 924px (57.75rem), and the minimum width at the
              24-character SKU cap is 57.5rem (`skuTableMinWidthRem`). Below
              it the table scrolls inside this region, never the page. The SKU
              column fits the longest code (in `ch` of the SKU font, so the
              `col` carries that font); Product takes what is left, down to
              its minimum. */}
          <table
            className="w-full table-fixed border-collapse text-sm"
            style={{ minWidth: `${skuTableMinWidthRem(allScope ? 'all' : 'one', skuChars, lineColumn)}rem` }}
          >
            <colgroup>
              {allScope && <col className="w-[7.25rem]" />}
              <col className="font-mono text-xs font-medium" style={{ width: `calc(${skuChars}ch + 1.5rem)` }} />
              <col />
              {lineColumn && <col className="w-[4.25rem]" />}
              <col className="w-[4.25rem]" />
              <col className="w-[4rem]" />
              <col className="w-[4rem]" />
              <col className="w-[4rem]" />
              <col className="w-[5.25rem]" />
              <col className="w-[5.75rem]" />
              <col className="w-[6rem]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b">
                {allScope && sortHeader('client', t('reporting.threePlUsage.client'), 'left')}
                {sortHeader('sku', t('reporting.threePlUsage.col.sku'), 'left')}
                {sortHeader('product', t('reporting.threePlUsage.col.product'), 'left')}
                {lineColumn && sortHeader('line', t('reporting.threePlUsage.col.thisLine'))}
                {sortHeader('shipped', t('reporting.threePlUsage.shipped'))}
                {sortHeader('printed', t('reporting.threePlUsage.printed'))}
                {sortHeader('pickPack', t('reporting.threePlUsage.pickPack'))}
                {sortHeader('blank', t('reporting.threePlUsage.col.blank'))}
                {sortHeader('ownerStock', t('reporting.threePlUsage.ownerStock', { owner: ownerName }))}
                {sortHeader('notShipped', t('reporting.threePlUsage.notShipped'))}
                {sortHeader(
                  'amount',
                  lineColumn ? t('reporting.threePlUsage.col.skuTotal') : t('reporting.threePlUsage.bill.amount'),
                )}
              </tr>
            </thead>
            <tbody>
              {billable.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="py-12 text-center text-sm text-muted-foreground">
                    {t('reporting.threePlUsage.sku.empty')}
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="py-12 text-center">
                    <p className="text-sm text-muted-foreground">{t('reporting.threePlUsage.sku.noMatch')}</p>
                    {searchText !== '' && (
                      <Button
                        type="button"
                        variant="link"
                        className="mt-1 h-auto p-0 text-xs"
                        onClick={clearSearch}
                      >
                        {t('reporting.threePlUsage.sku.clearSearch')}
                      </Button>
                    )}
                  </td>
                </tr>
              ) : (
                <>
                  {paddingTop > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingTop }} />
                    </tr>
                  )}
                  {virtualRows.map((virtualRow) => {
                    const row = sorted[virtualRow.index];
                    return row ? renderRow(row, virtualRow.index) : null;
                  })}
                  {paddingBottom > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingBottom }} />
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {t('reporting.threePlUsage.count.skus', { count: sorted.length })}
          </span>
          <span>{t('reporting.threePlUsage.sku.csvNote')}</span>
        </div>
        {lineColumn && (
          <p className="max-w-prose text-xs text-muted-foreground">
            {t('reporting.threePlUsage.sku.lineNote')}
            {filmOnly > 0 && <> {t('reporting.threePlUsage.sku.filmOnly', { count: filmOnly })}</>}
          </p>
        )}
        <p className="max-w-prose text-xs text-muted-foreground">
          {t('reporting.threePlUsage.sku.amountFooter')} {t('reporting.threePlUsage.excluded')}{' '}
          {t('reporting.threePlUsage.cancelDating')}
        </p>
      </section>
    </div>
  );
};
